import React, { useMemo, useEffect, useState, useRef } from "react";
import { Drawer, Box, Typography, Divider } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@store/index";
import {
  chartConfigSelector,
  chartConfigDispatcher,
  getCategoryRollupScope,
} from "@store/slices/chart-config-slice";
import type { WidgetRollup } from "@models/Rollup";
import {
  chartDrawerSelector,
  chartDrawerDispatcher,
} from "@store/slices/chart-drawer-slice";
import { useGetDatasetByIdQuery, useGetWeightConfigurationsQuery } from "@store/api/dataset-api";
import {
  buildDefaultDateGuardrails,
  type PointInTimeUnit,
  type GuardrailRollingOption,
  type ViewerSelectableTimePeriod,
  type PointInTimeViewerOptions,
} from "@models/Dataset";
import { updateViewerOptions } from "@store/slices/chart-config-slice";
import PointInTimeViewerOptionsSection from "../PointInTimeViewerOptionsSection";
import { useBarChartData } from "./hooks/useBarChartData";
import { useBarChartHandlers } from "./hooks/useBarChartHandlers";
import { useBarChartDerivedState } from "./hooks/useBarChartDerivedState";
import DrawerHeader from "./components/DrawerHeader";
import OrientationSection from "./components/OrientationSection";
import FormattingSection from "./components/FormattingSection";
import DisplayNSection from "./components/DisplayNSection";
import DataSortingSection from "./components/DataSortingSection";
import BarCustomizationSection from "./components/BarCustomizationSection";
import CategoryRollupsSection from "@components/charts/CategoryRollupsSection";
import CategoryRollupsNotice from "@components/charts/CategoryRollupsNotice";
import FocusModeSection from "./components/FocusModeSection";
import TimePeriodSection from "./components/TimePeriodSection";
import StatisticalTestingSection from "./components/StatisticalTestingSection";
import StylingSection from "./components/StylingSection";
import PreviewPanel from "./components/PreviewPanel";
import DrawerFooter from "./components/DrawerFooter";

const ALLOWED_ROLLUP_NAME_CHARS = /^[a-zA-Z0-9 _\-]*$/;

const BarChartConfigDrawer: React.FC = () => {
  const dispatch = useAppDispatch();
  const { currentConfig, datasetName, activeFilters, datasetId } =
    useAppSelector(chartConfigSelector);
  const { isDrawerOpen, editingWidgetId } = useAppSelector(chartDrawerSelector);

  const { data: datasetDetails } = useGetDatasetByIdQuery(datasetId as string, {
    skip: !datasetId,
  });

  // ─── Point-in-Time Date Guardrails ────────────────────────────────────────
  const pitGuardrails = useMemo(() => {
    return (
      datasetDetails?.dateGuardrails?.pointInTime ??
      buildDefaultDateGuardrails().pointInTime
    );
  }, [datasetDetails?.dateGuardrails]);

  const currentPitUnit = useMemo((): PointInTimeUnit | null => {
    const tp = currentConfig.timePeriodSelection?.timePeriod;
    if (tp === "latest_quarter" || tp === "previous_quarter") return "quarter";
    if (tp === "latest_semester" || tp === "previous_semester")
      return "semester";
    return null;
  }, [currentConfig.timePeriodSelection?.timePeriod]);

  const pitUnitGuardrail = currentPitUnit
    ? pitGuardrails.units[currentPitUnit]
    : null;

  const pitGuardrailAllowedWindowSizes: number[] | undefined =
    pitUnitGuardrail?.allowedRollingAverages.filter((n) => n !== 0);

  const pitGuardrailNoRollingDisabled: boolean = pitUnitGuardrail
    ? !pitUnitGuardrail.allowedRollingAverages.includes(
        0 as GuardrailRollingOption,
      )
    : false;

  // When the dataset only allows "No Rolling Average" for the current PIT unit,
  // the "Apply Rolling Average" checkbox should be disabled & forced off.
  const pitGuardrailRollingDisabled: boolean =
    !!pitUnitGuardrail && (pitGuardrailAllowedWindowSizes?.length ?? 0) === 0;

  const pitGuardrailConflict = useMemo((): string | null => {
    if (!pitUnitGuardrail || !currentPitUnit) return null;
    const isRollingEnabled = currentConfig.rollingAverage?.enabled ?? false;
    const windowSize = (currentConfig.rollingAverage?.windowSize ??
      0) as GuardrailRollingOption;
    const effectiveOption: GuardrailRollingOption = isRollingEnabled
      ? windowSize
      : 0;
    if (!pitUnitGuardrail.allowedRollingAverages.includes(effectiveOption)) {
      return isRollingEnabled
        ? `Rolling average of ${windowSize} periods is no longer allowed for ${currentPitUnit} by dataset policy.`
        : `No Rolling Average is no longer allowed for ${currentPitUnit} by dataset policy.`;
    }
    return null;
  }, [pitUnitGuardrail, currentPitUnit, currentConfig.rollingAverage]);

  const { closeChartDrawer } = chartDrawerDispatcher(dispatch);
  const {
    updateOrientation,
    updatePercentageFormatting,
    updateValuePlacement,
    updateDataSorting,
    updateTopNResponses,
    updateCombineRemainingIntoOther,
    updateColorPalette,
    updateLabelsPlacement,
    updateShowLegend,
    updateTruncateCategoryText,
    updateShowValueAxis,
    updateCompactMode,
    updateChartTitle,
    updateChartSubtitle,
    updateTitleBold,
    updateTitleUnderline,
    updateFocusSelection,
    updateFocusRollup,
    updateStatTestingEnabled,
    updateStatTestType,
    updateSignificanceLevel,
    updateMinimumSampleSize,
    updateWeight,
    updateBarHidden,
    updateBarPin,
    updateBarLabelOverride,
    updateBarManualColor,
    updateBarCustomOrder,
    updateShowBarN,
    updateShowChartN,
    addCategoryRollup,
    updateCategoryRollup,
    removeCategoryRollup,
  } = chartConfigDispatcher(dispatch);

  const { data: weightConfigData } = useGetWeightConfigurationsQuery(
    datasetId as string,
    { skip: !datasetId },
  );

  // When creating a new chart, default the weight to DATASET_DEFAULT if the
  // selected dataset has at least one weight configuration.
  useEffect(() => {
    if (
      editingWidgetId === "new-widget" &&
      (weightConfigData?.weightConfigurations?.length ?? 0) > 0 &&
      currentConfig.weight === "DISABLED"
    ) {
      updateWeight("DATASET_DEFAULT");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingWidgetId, weightConfigData]);

  const {
    fetchChartData,
    isRefetchingData,
    weightsApplied,
    setWeightsApplied,
  } = useBarChartData();

  const {
    localApplyFilters,
    isTogglingFilters,
    debouncedApplyFilters,
    usePreviewDateFilter,
    handleFilterToggle,
    handlePreviewDateFilterToggle,
    handleWeightChange,
    handleTimePeriodChange,
    handleTimePeriodDatesChange,
    handleStatTestingChange,
    handleRollingAverageEnabledChange,
    handleRollingAverageWindowSizeChange,
    handleSave,
    handleDiscard,
    isCreatingChart,
    updateFocusParams,
  } = useBarChartHandlers({
    fetchChartData,
    isRefetchingData,
    setWeightsApplied,
    pitGuardrails,
  });

  const {
    questions,
    allVariables,
    allValues,
    isAvailableData,
    canSave,
    hasSampleSizeBelowThreshold,
    hasPartialHistoricalData,
    hasNoPreviousPeriodData,
    headerTitle,
    isAllTimePeriod,
  } = useBarChartDerivedState(isRefetchingData, datasetDetails?.dataAvailabilityDate);

  const isDisabled = isRefetchingData || isTogglingFilters;

  // ─── Rollup editing state ─────────────────────────────────────────────────
  const [localFocusMetric, setLocalFocusMetric] = useState<"single" | "rollup">("single");
  const rollupIdRef = useRef<string>(currentConfig.focusRollup?.id ?? crypto.randomUUID());
  const savedRollupRef = useRef<{ rollup: WidgetRollup; name: string } | null>(null);
  const savedSingleRef = useRef<{ focusMode: string; focusEntity: string; focusEntityDisplayLabel?: string } | null>(null);

  useEffect(() => {
    if (isDrawerOpen) {
      const isRollup = !!currentConfig.focusRollup;
      setLocalFocusMetric(isRollup ? "rollup" : "single");
      rollupIdRef.current = currentConfig.focusRollup?.id ?? crypto.randomUUID();
      savedRollupRef.current = null;
      savedSingleRef.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawerOpen, editingWidgetId]);

  const focusLevel = useMemo((): "variable" | "value" => {
    const raw = currentConfig.focusMode?.toLowerCase();
    return raw === "value" ? "value" : "variable";
  }, [currentConfig.focusMode]);

  const selectedVariableId = useMemo(() => {
    if (localFocusMetric === "rollup" || focusLevel !== "variable") return "";
    return allVariables.find((v: any) => v.label === currentConfig.focusEntity)?.datasetElementId ?? "";
  }, [localFocusMetric, focusLevel, allVariables, currentConfig.focusEntity]);

  const selectedValueId = useMemo(() => {
    if (localFocusMetric === "rollup" || focusLevel !== "value") return "";
    return allValues.find((v: any) => String(v.value) === currentConfig.focusEntity)?.datasetElementId ?? "";
  }, [localFocusMetric, focusLevel, allValues, currentConfig.focusEntity]);

  const isFocusValid = useMemo(() => {
    if (!currentConfig.focusMode && !currentConfig.focusRollup) return true;

    if (localFocusMetric === "rollup") {
      const trimmed = (currentConfig.focusRollupName ?? "").trim();
      if (!trimmed || trimmed.length > 100 || !ALLOWED_ROLLUP_NAME_CHARS.test(trimmed)) return false;
      const lower = trimmed.toLowerCase();
      const hasConflict = (items: any[]) =>
        items.some(
          (item: any) =>
            item.title?.trim().toLowerCase() === lower ||
            item.rowtitle?.trim().toLowerCase() === lower ||
            item.label?.trim().toLowerCase() === lower,
        );
      if (hasConflict(allVariables) || hasConflict(allValues)) return false;
      if ((currentConfig.focusRollup?.sourceCategoryLabels?.length ?? 0) < 2) return false;
      return true;
    }
    return !!currentConfig.focusEntity;
  }, [
    localFocusMetric,
    currentConfig.focusMode,
    currentConfig.focusRollupName,
    currentConfig.focusRollup,
    currentConfig.focusEntity,
    allVariables,
    allValues,
  ]);

  const handleFocusLevelChange = (level: "variable" | "value") => {
    const upperLevel = level.toUpperCase();
    updateFocusParams({ focusMode: upperLevel, focusEntity: "" });
    if (localFocusMetric === "rollup") {
      handleRollupLabelsChange([]);
    }
  };

  const handleFocusMetricChange = (metric: "single" | "rollup") => {
    setLocalFocusMetric(metric);
    if (metric === "rollup") {
      if (currentConfig.focusEntity) {
        savedSingleRef.current = {
          focusMode: currentConfig.focusMode ?? "VARIABLE",
          focusEntity: currentConfig.focusEntity,
          focusEntityDisplayLabel: currentConfig.focusEntityDisplayLabel,
        };
      }
      if (savedRollupRef.current) {
        rollupIdRef.current = savedRollupRef.current.rollup.id;
        updateFocusRollup(savedRollupRef.current);
        if ((savedRollupRef.current.rollup.sourceCategoryLabels?.length ?? 0) >= 2) {
          fetchChartData(currentConfig.weight, debouncedApplyFilters, undefined, undefined, undefined, false, savedRollupRef.current.rollup);
        }
        savedRollupRef.current = null;
      } else {
        rollupIdRef.current = crypto.randomUUID();
        updateFocusParams({ focusMode: currentConfig.focusMode ?? "VARIABLE", focusEntity: "" });
        updateFocusRollup(null);
      }
    } else {
      if (currentConfig.focusRollup) {
        savedRollupRef.current = {
          rollup: currentConfig.focusRollup,
          name: currentConfig.focusRollupName ?? "",
        };
      }
      updateFocusRollup(null);
      if (savedSingleRef.current) {
        updateFocusParams(savedSingleRef.current);
        fetchChartData(
          currentConfig.weight, debouncedApplyFilters,
          undefined, undefined, undefined, false,
          undefined,
          { focusMode: savedSingleRef.current.focusMode, focusEntity: savedSingleRef.current.focusEntity },
        );
        savedSingleRef.current = null;
      } else {
        updateFocusParams({ focusMode: currentConfig.focusMode ?? "VARIABLE", focusEntity: "" });
        updateFocusSelection({ mode: focusLevel });
      }
    }
  };

  const handleSingleFocusParamsChange = (params: {
    focusMode: string;
    focusEntity: string;
    focusEntityDisplayLabel?: string;
  } | null) => {
    if (!params) return;
    updateFocusParams(params);
    fetchChartData(
      currentConfig.weight,
      debouncedApplyFilters,
      undefined, undefined, undefined, false,
      undefined,
      { focusMode: params.focusMode, focusEntity: params.focusEntity },
    );
  };

  const handleRollupNameChange = (name: string) => {
    const labels = currentConfig.focusRollup?.sourceCategoryLabels ?? [];
    updateFocusRollup({
      rollup: { id: rollupIdRef.current, calculationType: "SUM_SELECTED", sourceCategoryLabels: labels },
      name,
    });
  };

  const handleRollupLabelsChange = (labels: string[]) => {
    const newRollup = {
      id: rollupIdRef.current,
      calculationType: "SUM_SELECTED" as const,
      sourceCategoryLabels: labels,
    };
    updateFocusRollup({ rollup: newRollup, name: currentConfig.focusRollupName ?? "" });
    if (labels.length >= 2) {
      fetchChartData(currentConfig.weight, debouncedApplyFilters, undefined, undefined, undefined, false, newRollup);
    }
  };

  const activeCategoryRollups = useMemo(() => {
    const scope = getCategoryRollupScope(currentConfig.focusMode);
    return currentConfig.categoryRollupsByScope?.[scope] ?? [];
  }, [currentConfig.focusMode, currentConfig.categoryRollupsByScope]);

  const categoryRollupBarOptions = useMemo(() => {
    const rollupBarNames = new Set(
      activeCategoryRollups
        .map((r) => r.name)
        .filter((n): n is string => !!n),
    );

    // Non-rollup bars visible in the current series.
    const options: Array<{
      sourceLabel: string;
      displayLabel: string;
      originalLabel?: string;
    }> = currentConfig.series
      .filter((s) => !rollupBarNames.has(s.name))
      .map((s) => {
        const displayLabel =
          currentConfig.barCustomization.labelOverrides[s.name] ?? s.name;
        return {
          sourceLabel: s.variableId ?? s.name,
          displayLabel,
          originalLabel: displayLabel !== s.name ? s.name : undefined,
        };
      });

    // Source bars absorbed by rollups are absent from currentConfig.series.
    // Re-add them using variableIdLabelMap so they appear as selectable/deselectable
    // options when editing a rollup.
    const presentLabels = new Set(options.map((o) => o.sourceLabel));
    for (const rollup of activeCategoryRollups) {
      for (const variableId of rollup.sourceCategoryLabels) {
        if (!presentLabels.has(variableId)) {
          const originalName = currentConfig.variableIdLabelMap?.[variableId];
          if (originalName) {
            const displayLabel =
              currentConfig.barCustomization.labelOverrides[originalName] ??
              originalName;
            options.push({
              sourceLabel: variableId,
              displayLabel,
              originalLabel:
                displayLabel !== originalName ? originalName : undefined,
            });
            presentLabels.add(variableId);
          }
        }
      }
    }

    return options;
  }, [
    currentConfig.series,
    currentConfig.barCustomization.labelOverrides,
    currentConfig.variableIdLabelMap,
    activeCategoryRollups,
  ]);

  const handleCategoryRollupCreate = (rollup: WidgetRollup) => {
    addCategoryRollup(rollup);
    const updatedRollups = [...activeCategoryRollups, rollup];
    fetchChartData(currentConfig.weight, debouncedApplyFilters, undefined, undefined, undefined, false, undefined, undefined, updatedRollups);
  };

  const handleCategoryRollupUpdate = (rollup: WidgetRollup) => {
    updateCategoryRollup(rollup);
    const updatedRollups = activeCategoryRollups.map((r) =>
      r.id === rollup.id ? rollup : r,
    );
    fetchChartData(currentConfig.weight, debouncedApplyFilters, undefined, undefined, undefined, false, undefined, undefined, updatedRollups);
  };

  const handleCategoryRollupDelete = (rollupId: string) => {
    removeCategoryRollup(rollupId);
    const updatedRollups = activeCategoryRollups.filter((r) => r.id !== rollupId);
    fetchChartData(currentConfig.weight, debouncedApplyFilters, undefined, undefined, undefined, false, undefined, undefined, updatedRollups);
  };


  const isViewerOptionsInvalid =
    currentPitUnit !== null &&
    currentConfig.viewerOptions.enabled &&
    Object.keys(currentConfig.viewerOptions.additionalPeriods).length === 0 &&
    currentConfig.viewerOptions.chartPeriodAdditionalRollingAverages.length ===
      0;

  const dashboardDateOverride = useMemo(() => {
    if (!usePreviewDateFilter) return undefined;
    const params = new URLSearchParams(window.location.search);
    const dfStart = params.get("df_start");
    const dfEnd = params.get("df_end");
    if (dfStart && dfEnd) {
      return { startDate: dfStart, endDate: dfEnd };
    }
    return undefined;
  }, [usePreviewDateFilter]);

  return (
    <Drawer
      anchor="right"
      open={isDrawerOpen}
      onClose={() => closeChartDrawer()}
      sx={{
        "& .MuiDrawer-paper": {
          width: "80vw",
          boxSizing: "border-box",
        },
      }}
    >
      <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <DrawerHeader
          questionLabel={questions?.label}
          headerTitle={headerTitle}
          datasetName={datasetName}
          activeFiltersCount={activeFilters.length}
          localApplyFilters={localApplyFilters}
          isTogglingFilters={isTogglingFilters}
          isRefetchingData={isRefetchingData}
          onFilterToggle={handleFilterToggle}
          usePreviewDateFilter={usePreviewDateFilter}
          onPreviewDateFilterToggle={handlePreviewDateFilterToggle}
        />

        <Box sx={{ flex: 1, display: "flex", overflow: "hidden" }}>
          {/* Configuration Panel */}
          <Box
            sx={{
              width: 400,
              borderRight: 1,
              borderColor: "divider",
              overflow: "auto",
              p: 3,
            }}
          >
            <Typography variant="h6" sx={{ mb: 3, fontWeight: 700 }}>
              Configurations
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              <OrientationSection
                orientation={currentConfig.orientation}
                onOrientationChange={updateOrientation}
              />

              <FormattingSection
                percentageFormatting={currentConfig.percentageFormatting}
                valuePlacement={currentConfig.valuePlacement}
                weight={currentConfig.weight}
                weightsApplied={weightsApplied}
                isRefetchingData={isRefetchingData}
                onPercentageFormattingChange={updatePercentageFormatting}
                onValuePlacementChange={updateValuePlacement}
                onWeightChange={handleWeightChange}
              />

              <DataSortingSection
                dataSorting={currentConfig.dataSorting}
                topNResponses={currentConfig.topNResponses}
                topNValue={currentConfig.topNValue}
                combineRemainingIntoOther={
                  currentConfig.combineRemainingIntoOther
                }
                onDataSortingChange={updateDataSorting}
                onTopNResponsesChange={updateTopNResponses}
                onCombineRemainingChange={updateCombineRemainingIntoOther}
              />

              <Divider />

              {(currentConfig.focusMode || currentConfig.focusRollup) && (
                  <FocusModeSection
                    focusLevel={focusLevel}
                    allVariables={allVariables}
                    allValues={allValues}
                    isDisabled={isDisabled}
                    focusMetric={localFocusMetric}
                    selectedVariableId={selectedVariableId}
                    selectedValueId={selectedValueId}
                    rollupName={currentConfig.focusRollupName ?? ""}
                    rollupSelectedLabels={
                      currentConfig.focusRollup?.sourceCategoryLabels ?? []
                    }
                    onFocusLevelChange={handleFocusLevelChange}
                    onFocusParamsChange={handleSingleFocusParamsChange}
                    onFocusMetricChange={handleFocusMetricChange}
                    onRollupNameChange={handleRollupNameChange}
                    onRollupLabelsChange={handleRollupLabelsChange}
                  />
                )}

              <TimePeriodSection
                timePeriodSelection={currentConfig.timePeriodSelection}
                rollingAverage={currentConfig.rollingAverage}
                statisticalTestingEnabled={
                  currentConfig.statisticalTesting?.enabled || false
                }
                responsesMinDate={currentConfig.responsesMinDate}
                responsesMaxDate={currentConfig.responsesMaxDate}
                isDisabled={isDisabled}
                totalAvailablePeriods={currentConfig.categories?.length}
                onTimePeriodChange={handleTimePeriodChange}
                onTimePeriodDatesChange={handleTimePeriodDatesChange}
                onRollingAverageEnabledChange={
                  handleRollingAverageEnabledChange
                }
                onRollingAverageWindowSizeChange={
                  handleRollingAverageWindowSizeChange
                }
                guardrailAllowedWindowSizes={pitGuardrailAllowedWindowSizes}
                guardrailNoRollingDisabled={pitGuardrailNoRollingDisabled}
                guardrailRollingDisabled={pitGuardrailRollingDisabled}
                guardrailConflictWarning={pitGuardrailConflict ?? undefined}
              />

              {currentPitUnit && (
                <>
                  <Divider />
                  <PointInTimeViewerOptionsSection
                    viewerOptions={currentConfig.viewerOptions}
                    chartTimePeriod={
                      currentConfig.timePeriodSelection
                        ?.timePeriod as ViewerSelectableTimePeriod
                    }
                    chartRollingWindowSize={
                      currentConfig.rollingAverage?.enabled
                        ? (currentConfig.rollingAverage?.windowSize ?? 0)
                        : 0
                    }
                    pitGuardrailUnits={pitGuardrails.units}
                    isDisabled={isDisabled}
                    onViewerOptionsChange={(opts: PointInTimeViewerOptions) =>
                      dispatch(updateViewerOptions(opts))
                    }
                  />
                </>
              )}

              <CategoryRollupsSection
                categoryRollups={activeCategoryRollups}
                barOptions={categoryRollupBarOptions}
                onCreateRollup={handleCategoryRollupCreate}
                onUpdateRollup={handleCategoryRollupUpdate}
                onDeleteRollup={handleCategoryRollupDelete}
              />

              <CategoryRollupsNotice
                categoryRollups={activeCategoryRollups}
                barOptions={categoryRollupBarOptions}
                variableIdLabelMap={currentConfig.variableIdLabelMap}
                labelOverrides={currentConfig.barCustomization.labelOverrides}
              />

              <BarCustomizationSection
                bars={currentConfig.series.map((s) => ({
                  name: s.name,
                  color: s.color || "#57bfe7",
                }))}
                barCustomization={currentConfig.barCustomization}
                dataSorting={currentConfig.dataSorting}
                colorPalette={currentConfig.colorPalette}
                onToggleVisibility={(barName) => {
                  const isHidden =
                    currentConfig.barCustomization.hiddenBars.includes(barName);
                  updateBarHidden({ barName, hidden: !isHidden });
                }}
                onToggleAll={(visible) => {
                  if (visible) {
                    // Show all: clear hiddenBars
                    currentConfig.barCustomization.hiddenBars.forEach(
                      (barName) =>
                        updateBarHidden({ barName, hidden: false }),
                    );
                  } else {
                    // Hide all except one: hide all but first visible
                    const visibleBars = currentConfig.series.filter(
                      (s) =>
                        !currentConfig.barCustomization.hiddenBars.includes(
                          s.name,
                        ),
                    );
                    visibleBars
                      .slice(1)
                      .forEach((s) =>
                        updateBarHidden({ barName: s.name, hidden: true }),
                      );
                  }
                }}
                onPinChange={(barName, position) =>
                  updateBarPin({ barName, position })
                }
                onLabelChange={(barName, label) =>
                  updateBarLabelOverride({ barName, label })
                }
                onResetLabel={(barName) =>
                  updateBarLabelOverride({ barName, label: barName })
                }
                onCustomOrderChange={(newOrder) =>
                  updateBarCustomOrder(newOrder)
                }
              />

              <DisplayNSection
                showBarN={currentConfig.showBarN}
                showChartN={currentConfig.showChartN}
                onShowBarNChange={updateShowBarN}
                onShowChartNChange={updateShowChartN}
              />

              <Divider />

              <StatisticalTestingSection
                statisticalTesting={currentConfig.statisticalTesting}
                isDisabled={isDisabled}
                isAllTimePeriod={isAllTimePeriod}
                rollingAverageEnabled={
                  currentConfig.rollingAverage?.enabled || false
                }
                onStatTestingChange={handleStatTestingChange}
                updateStatTestingEnabled={updateStatTestingEnabled}
                updateStatTestType={updateStatTestType}
                updateSignificanceLevel={updateSignificanceLevel}
                updateMinimumSampleSize={updateMinimumSampleSize}
              />

              <Divider />

              <StylingSection
                title={currentConfig.title}
                subtitle={currentConfig.subtitle}
                titleBold={currentConfig.titleBold}
                titleUnderline={currentConfig.titleUnderline}
                colorPalette={currentConfig.colorPalette}
                labelsPlacement={currentConfig.labelsPlacement}
                showLegend={currentConfig.showLegend}
                truncateCategoryText={currentConfig.truncateCategoryText}
                showValueAxis={currentConfig.showValueAxis}
                compactMode={currentConfig.compactMode}
                barColorEntries={currentConfig.series
                  .filter(
                    (s) =>
                      !currentConfig.barCustomization.hiddenBars.includes(
                        s.name,
                      ),
                  )
                  .map((s) => ({
                    name: s.name,
                    displayName:
                      currentConfig.barCustomization.labelOverrides[s.name] ??
                      s.name,
                    fallbackColor: s.color || "#57bfe7",
                  }))}
                manualColors={currentConfig.barCustomization.manualColors}
                onManualColorChange={(barName, color) =>
                  updateBarManualColor({ barName, color })
                }
                onTitleChange={updateChartTitle}
                onSubtitleChange={updateChartSubtitle}
                onTitleBoldChange={updateTitleBold}
                onTitleUnderlineChange={updateTitleUnderline}
                onColorPaletteChange={updateColorPalette}
                onLabelsPlacementChange={updateLabelsPlacement}
                onShowLegendChange={updateShowLegend}
                onTruncateCategoryTextChange={updateTruncateCategoryText}
                onShowValueAxisChange={updateShowValueAxis}
                onCompactModeChange={updateCompactMode}
              />
            </Box>
          </Box>

          {/* Preview Panel */}
          <PreviewPanel
            currentConfig={currentConfig}
            questions={questions}
            isRefetchingData={isRefetchingData}
            isAvailableData={isAvailableData}
            hasSampleSizeBelowThreshold={hasSampleSizeBelowThreshold}
            hasPartialHistoricalData={hasPartialHistoricalData}
            hasNoPreviousPeriodData={hasNoPreviousPeriodData}
            dashboardDateOverride={dashboardDateOverride}
            dataAvailabilityDate={datasetDetails?.dataAvailabilityDate}
          />
        </Box>

        <DrawerFooter
          isCreatingChart={isCreatingChart}
          canSave={canSave && !pitGuardrailConflict && !isViewerOptionsInvalid && isFocusValid}
          isRefetchingData={isRefetchingData}
          onSave={handleSave}
          onDiscard={handleDiscard}
        />
      </Box>
    </Drawer>
  );
};

export default BarChartConfigDrawer;
